import { passwordClient } from 'api';
import type { AxiosError } from 'axios';
import useAuth from 'common/useAuth';
import { LoadingIndicator, PasswordForm } from 'components';
import { PublicPage } from 'components/Layout';
import type { PasswordFormValues } from 'components/PasswordForm/PasswordForm';
import { useCustomTranslation } from 'helpers/translationHelper';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { PasswordToCreate } from './models';

type RouteParams = 'token' | 'userId';

const RecoveryPassword: React.FC = (): React.ReactElement => {
  const { t } = useCustomTranslation();

  const { login, loading } = useAuth();
  const params = useParams<RouteParams>();
  const navigate = useNavigate();

  const handleSubmit = async (values: PasswordFormValues) => {
    const { token, userId } = params;

    try {
      const submitValues: PasswordToCreate = {
        newPassword: values.newPassword,
        token: token || '',
        userId: userId || ''
      };
      const result = await passwordClient.recoverPassword(submitValues);

      await login({
        username: result.data.username,
        password: values.newPassword,
        customTenantCode: result.data.tenantCode
      });
      navigate('/');
    } catch (e) {
      if ((e as AxiosError).response?.status === 404) {
        throw {
          response: {
            status: 400,
            data: { errorCode: 'passwordLinkExpired' }
          }
        };
      }

      throw e;
    }
  };

  const renderPasswordForm = () => {
    return (
      <>
        <div className="RecoveryPassword_content-heading heading">
          {t('password.recoveryPasswordHeading')}
        </div>
        <PasswordForm onSubmit={handleSubmit} />
      </>
    );
  };

  const renderLoginAnimation = () => {
    return (
      <>
        <div className="RecoveryPassword_content-heading heading">
          {t('password.loginFromPasswordHeading')}
        </div>
        <LoadingIndicator />
      </>
    );
  };

  return (
    <PublicPage className="RecoveryPassword" title={t('password.recoverTitle')}>
      <div className="RecoveryPassword_content white-box">
        {loading ? renderLoginAnimation() : renderPasswordForm()}
      </div>
    </PublicPage>
  );
};

export default RecoveryPassword;
